import React from "react";
import { legalCompanyDetails } from "./constants";
import type { LegalDocumentDefinition } from "./types";

const {
  brandName,
  contactEmail,
  legalEntityName,
  privacyEmail,
  registeredAddress,
  serviceName,
} = legalCompanyDetails;

export const privacyDocument: LegalDocumentDefinition = {
  id: "privacy",
  title: "Privacy Policy",
  slug: "/privacy",
  effectiveDate: "2026-06-13",
  render: (renderLink) => (
    <>
      <p>
        This Privacy Policy explains how {brandName} collects, uses, shares, and protects personal data when you use
        {serviceName}. It also explains your choices and rights.
      </p>

      <h5>1. Who we are</h5>
      <p>
        The controller for personal data is <strong>{legalEntityName}</strong>. Registered address:{" "}
        <strong>{registeredAddress}</strong>. You can contact us about privacy matters at <code>{privacyEmail}</code> or
        general questions at <code>{contactEmail}</code>.
      </p>

      <h5>2. Information we collect</h5>
      <p>We collect and process the following categories of information:</p>
      <ul>
        <li>
          <strong>Account data:</strong> name, email address, sign-in details, household membership, account settings,
          and preferences.
        </li>
        <li>
          <strong>Household and content data:</strong> recipes, meal plans, shopping lists, pantry or ingredient data,
          comments, messages, ratings, tags, allergens, nutrition-related inputs, and other content you add.
        </li>
        <li>
          <strong>Usage and device data:</strong> pages viewed, actions taken, feature usage, approximate location
          derived from IP address, browser, device, operating system, and app version.
        </li>
        <li>
          <strong>Logs and diagnostics:</strong> IP address, timestamps, errors, crash reports, performance data, and
          security or abuse signals.
        </li>
        <li>
          <strong>Communications:</strong> support requests, contact messages, feedback, and related correspondence.
        </li>
        <li>
          <strong>Cookie and analytics data:</strong> cookie consent choices, necessary cookies, and optional analytics
          data where enabled. See our {renderLink({ href: "/cookies", children: "Cookie Policy" })}.
        </li>
      </ul>

      <h5>3. How we use information</h5>
      <ul>
        <li>Provide, maintain, secure, and improve the service.</li>
        <li>Set up accounts, households, preferences, recipes, meal plans, shopping lists, and related features.</li>
        <li>Respond to support requests, contact messages, and user feedback.</li>
        <li>Monitor performance, debug issues, prevent abuse, and protect the reliability and security of the service.</li>
        <li>Measure product usage where optional analytics are accepted.</li>
        <li>Develop and operate optional AI-assisted features where available.</li>
        <li>Comply with legal obligations and enforce our {renderLink({ href: "/tos", children: "Terms of Service" })}.</li>
      </ul>

      <h5>4. Legal bases for UK and EEA users</h5>
      <p>Where UK or EEA data protection law applies, we rely on the following legal bases, mapped to the purpose of processing:</p>
      <ul>
        <li><strong>Account and service delivery:</strong> performance of a contract.</li>
        <li><strong>Security and error monitoring:</strong> legitimate interests in protecting the reliability and security of the service.</li>
        <li><strong>Optional analytics:</strong> consent, which you can withdraw at any time. See our {renderLink({ href: "/cookies", children: "Cookie Policy" })}.</li>
        <li><strong>Legal compliance:</strong> compliance with a legal obligation.</li>
        <li>
          <strong>Health-related data</strong> (such as allergens or nutrition-related inputs you choose to add), where
          applicable: your explicit consent, as the applicable condition for processing special category data under
          Article 9 of the UK GDPR.
        </li>
      </ul>

      <h5>5. Sharing and disclosure</h5>
      <p>We do not sell personal data. We may share personal data with:</p>
      <ul>
        <li>
          <strong>Service providers:</strong> vendors that help provide hosting, analytics, feature flags,
          AI-assisted features, communications, support, security, and error monitoring.
        </li>
        <li>
          <strong>Legal and safety recipients:</strong> regulators, authorities, advisers, or other parties where needed
          to comply with law, enforce terms, protect rights, investigate abuse, or protect users and systems.
        </li>
        <li>
          <strong>Business transfer recipients:</strong> parties involved in a merger, acquisition, financing,
          reorganisation, or sale of assets, subject to appropriate protections.
        </li>
      </ul>

      <h5>6. Vendors and subprocessors</h5>
      <p>We use the following providers to operate the service:</p>
      <ul>
        <li>
          <strong>Cloud hosting and infrastructure:</strong> we share personal data with a UK-based provider that
          stores, secures, backs up, and delivers the Service on our behalf.
        </li>
        <li>
          <strong>LaunchDarkly (feature flags):</strong> rollouts and configuration. See{" "}
          <a href="https://launchdarkly.com/policies/privacy/" target="_blank" rel="noreferrer">LaunchDarkly Privacy</a>.
        </li>
        <li>
          <strong>Heap Analytics (optional product analytics):</strong> usage measurement where accepted. See{" "}
          <a href="https://www.heap.io/privacy" target="_blank" rel="noreferrer">Heap Privacy</a>.
        </li>
        <li>
          <strong>Sentry (error monitoring):</strong> error and performance monitoring. See{" "}
          <a href="https://sentry.io/privacy/" target="_blank" rel="noreferrer">Sentry Privacy</a>.
        </li>
        <li>
          <strong>OpenAI (optional AI features):</strong> may provide models that power AI-assisted functionality where
          enabled. See{" "}
          <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noreferrer">OpenAI Privacy</a>.
        </li>
        <li>
          <strong>Google Cloud Vertex AI (optional AI features):</strong> may provide Gemini models that power
          AI-assisted functionality where enabled. See{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy</a>.
        </li>
        <li>
          <strong>Anthropic (optional AI features):</strong> may provide Claude models that power AI-assisted
          functionality where enabled. See{" "}
          <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer">Anthropic Privacy</a>.
        </li>
      </ul>

      <h5>7. Cookies and similar technologies</h5>
      <p>
        We and our providers use cookies, SDKs, local storage, and similar technologies for authentication, preferences,
        security, analytics, and reliability. Optional analytics are controlled through cookie preferences. Some
        necessary features may not function without essential cookies.
      </p>

      <h5>8. Data retention</h5>
      <p>
        We retain personal data for as long as needed to provide the service, comply with legal obligations, resolve
        disputes, enforce agreements, maintain security, and operate backups. We retain the following categories of
        data for the periods below:
      </p>
      <ul>
        <li>
          <strong>Product analytics:</strong> up to 6 months.
        </li>
        <li>
          <strong>Error and crash reports:</strong> up to 30 days.
        </li>
        <li>
          <strong>Application logs:</strong> up to approximately 6 months.
        </li>
        <li>
          <strong>Backups:</strong> up to 3 months.
        </li>
        <li>
          <strong>Account data after deletion:</strong> retained for 90 days after deletion to allow account recovery,
          after which personal information (name, email, and free-text content) is anonymized. Non-identifying
          billing and subscription records (transaction IDs, amounts, dates) are retained for accounting and legal
          purposes.
        </li>
      </ul>
      <p>You may delete content you create, such as recipes and meal plans, from within the app where deletion features are available.</p>

      <h5>9. Security</h5>
      <p>
        We use administrative, technical, and organisational measures designed to protect personal data, including
        encryption at rest for our database and backups. No transmission or storage system is completely secure, so
        we cannot guarantee absolute security.
      </p>

      <h5>10. International transfers</h5>
      <p>
        Our hosting infrastructure is UK-based, so most personal data does not leave the UK. However, some of the
        providers listed in Section 6 process personal data outside the UK:
      </p>
      <ul>
        <li>
          <strong>Google Cloud Vertex AI and Anthropic (optional AI features):</strong> processed in the EU
          (Belgium). Transfers from the UK to the EEA are covered by the UK&rsquo;s data protection adequacy
          regulations, so no additional contractual safeguard is required.
        </li>
        <li>
          <strong>OpenAI (optional AI features):</strong> processed in the United States. We rely on the EU
          Standard Contractual Clauses, as amended by the UK International Data Transfer Addendum, as the transfer
          safeguard.
        </li>
        <li>
          <strong>LaunchDarkly (feature flags) and Sentry (error monitoring):</strong> may process data in the
          United States. These providers primarily rely on their Data Privacy Framework certifications for
          transfers from the UK, with the EU Standard Contractual Clauses and UK International Data Transfer
          Addendum applying as a contractual fallback.
        </li>
        <li>
          <strong>Heap Analytics (optional product analytics):</strong> may process data outside the UK. We rely
          on the EU Standard Contractual Clauses and the UK International Data Transfer Addendum as the transfer
          safeguard.
        </li>
      </ul>
      <p>
        For details on the specific safeguard relied on for a particular transfer, contact us at{" "}
        <code>{privacyEmail}</code>.
      </p>

      <h5>11. Your rights</h5>
      <p>
        Depending on your location, you may have rights to access, correct, erase, restrict, object to, or export your
        personal data, and to withdraw consent where processing is based on consent. To exercise rights, contact{" "}
        <code>{privacyEmail}</code>. We may need to verify your request before responding.
      </p>
      <p>
        If UK data protection law applies, you may also complain to the UK Information Commissioner’s Office. See{" "}
        <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">how to complain to the ICO</a>.
      </p>

      <h5>12. Children’s privacy</h5>
      <p>
        {serviceName} is not directed to children and we do not knowingly collect personal data from children under 13
        or the age of digital consent in their country. If you believe a child has provided personal data, contact us so
        we can review and delete it where required.
      </p>

      <h5>13. Food, nutrition, and AI information</h5>
      <p>
        Some content may relate to recipes, ingredients, nutrition, allergies, or AI-assisted suggestions. This
        information is subject to our {renderLink({ href: "/disclaimer", children: "Disclaimer" })}.
      </p>

      <h5>14. Changes to this policy</h5>
      <p>
        We may update this policy from time to time. We will post the updated version here and revise the effective
        date. For material changes, we may provide additional notice.
      </p>
    </>
  ),
};
